// Landing-page SEO share-image uploader. Three-step flow per file:
//   1. POST /landing-pages/{id}/seo/image/sign   → presigned PUT URL
//   2. PUT bytes directly to the presigned URL
//   3. POST /landing-pages/{id}/seo/image/commit → returns { seo_image_url }
// "Remove" hits DELETE /landing-pages/{id}/seo/image. The widget is
// re-wired after every htmx swap, so the SEO popover gets enhanced when
// it loads.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-seo-image]";
const FALLBACK_UPLOAD_ERROR = "Could not upload your image. Please try again.";
const FALLBACK_REMOVE_ERROR = "Could not remove your image. Please try again.";

export function initSEOImage(root = document) {
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireWidget);
}

function wireWidget(widget) {
  if (!firstBind(widget)) return;

  const pageID = widget.dataset.seoImagePageId;
  const input = widget.querySelector("[data-seo-image-input]");
  const removeBtn = widget.querySelector("[data-seo-image-remove]");
  const preview = widget.querySelector("[data-seo-image-preview]");
  const placeholder = widget.querySelector("[data-seo-image-placeholder]");
  const errorEl = widget.querySelector("[data-seo-image-error]");
  const statusEl = widget.querySelector("[data-seo-image-status]");
  if (!pageID || !input) return;

  input.addEventListener("change", async () => {
    const file = input.files?.[0];
    if (!file) return;

    hide(errorEl);
    setStatus(statusEl, "Uploading…");
    input.disabled = true;

    try {
      const url = await uploadOne(pageID, file);
      showPreview(preview, placeholder, removeBtn, url);
    } catch (err) {
      showError(errorEl, errorMessage(err, FALLBACK_UPLOAD_ERROR));
    } finally {
      hide(statusEl);
      input.disabled = false;
      input.value = "";
    }
  });

  if (removeBtn) {
    removeBtn.addEventListener("click", async () => {
      hide(errorEl);
      removeBtn.disabled = true;
      setStatus(statusEl, "Removing…");

      try {
        const resp = await fetch(`/landing-pages/${pageID}/seo/image`, {
          method: "DELETE",
          credentials: "same-origin",
        });
        if (!resp.ok && resp.status !== 204)
          throw await asUserError(resp, FALLBACK_REMOVE_ERROR);
        clearPreview(preview, placeholder, removeBtn);
      } catch (err) {
        showError(errorEl, errorMessage(err, FALLBACK_REMOVE_ERROR));
      } finally {
        hide(statusEl);
        removeBtn.disabled = false;
      }
    });
  }
}

async function uploadOne(pageID, file) {
  const signParams = new URLSearchParams();
  signParams.append("filename", file.name);
  signParams.append("content_type", file.type || "application/octet-stream");
  signParams.append("file_size_bytes", String(file.size));

  const signResp = await fetch(`/landing-pages/${pageID}/seo/image/sign`, {
    method: "POST",
    body: signParams,
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!signResp.ok) throw await asUserError(signResp, FALLBACK_UPLOAD_ERROR);

  const signed = await signResp.json();
  if (!signed.upload_url || !signed.key) throw new Error(FALLBACK_UPLOAD_ERROR);

  // upload_headers are the signed metadata headers from the sign step;
  // S3 rejects the PUT signature unless they're echoed back verbatim.
  const putResp = await fetch(signed.upload_url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      ...(signed.upload_headers || {}),
    },
  });
  if (!putResp.ok) throw new Error(FALLBACK_UPLOAD_ERROR);

  const commitParams = new URLSearchParams();
  commitParams.append("key", signed.key);

  const commitResp = await fetch(`/landing-pages/${pageID}/seo/image/commit`, {
    method: "POST",
    body: commitParams,
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!commitResp.ok)
    throw await asUserError(commitResp, FALLBACK_UPLOAD_ERROR);

  const committed = await commitResp.json();
  return committed.seo_image_url || "";
}

function showPreview(preview, placeholder, removeBtn, url) {
  if (preview && url) {
    // Fade the freshly-uploaded image in once it loads, so it doesn't
    // pop in over the placeholder.
    preview.style.opacity = "0";
    preview.addEventListener(
      "load",
      () => {
        preview.style.opacity = "1";
      },
      { once: true },
    );
    preview.classList.remove("hidden");
    preview.src = url;
  }
  if (placeholder) placeholder.classList.add("hidden");
  if (removeBtn) removeBtn.classList.remove("hidden");
}

function clearPreview(preview, placeholder, removeBtn) {
  if (preview) {
    preview.removeAttribute("src");
    preview.classList.add("hidden");
  }
  if (placeholder) placeholder.classList.remove("hidden");
  if (removeBtn) removeBtn.classList.add("hidden");
}

function setStatus(el, text) {
  if (!el) return;
  el.textContent = text;
  el.classList.remove("hidden");
}

function hide(el) {
  if (el) el.classList.add("hidden");
}

function showError(el, text) {
  if (!el) return;
  el.textContent = text;
  el.classList.remove("hidden");
}

async function asUserError(resp, fallback) {
  try {
    const body = await resp.json();
    if (body && body.error) return new Error(body.error);
  } catch {
    /* fall through to the generic message */
  }
  return new Error(fallback);
}

function errorMessage(err, fallback) {
  return err && err.message ? err.message : fallback;
}
